// S3 선행 공통 스텝 — 비율 계산 SSOT.
// 종전: App.jsx RATIO_CALC ↔ B9Render.jsx ratio()가 문자 단위 동일 수식을 수동 동기화(주석 의존).
// 이 파일이 유일 정의 — 양쪽은 import만. aggCalc(App.jsx Dashboard 전용)는 반올림·0폴백이 달라
// 의도적으로 미통합(S3-1 북스타에서 화면 영향과 함께 결정).
// 분모 0 → null('—'). 결제시작률·구매완수율은 100% 초과가 정상(다이렉트 결제·기여 기간 차) — 클램프 금지.
// vtr/cpv_view = video_views 기반 — Google(TrueView)·TikTok(6초 시청) 합류, 정의 차이는 지표 desc 고지.
// (tiktok-video-metrics 배치 2 — META는 video_views=0 → null 불변.)

const RAW_RATIO_CALC = {
  ctr: (t) => (t.imp > 0 ? (t.click / t.imp) * 100 : null),
  cvr: (t) => (t.click > 0 ? (t.cv / t.click) * 100 : null),
  cpc: (t) => (t.click > 0 ? t.spend / t.click : null),
  cpm: (t) => (t.imp > 0 ? (t.spend / t.imp) * 1000 : null),
  cpa: (t) => (t.cv > 0 ? t.spend / t.cv : null),
  roas: (t) => (t.spend > 0 ? (t.rev / t.spend) * 100 : null),
  aov: (t) => (t.cv > 0 ? t.rev / t.cv : null),
  hook_rate: (t) =>
    t.imp > 0 ? ((t.video_views_3s || 0) / t.imp) * 100 : null,
  retention_rate: (t) =>
    t.video_plays > 0 ? ((t.thruplay || 0) / t.video_plays) * 100 : null,
  complete_rate: (t) =>
    t.video_plays > 0 ? ((t.video_p100 || 0) / t.video_plays) * 100 : null,
  cpv_3s: (t) => (t.video_views_3s > 0 ? t.spend / t.video_views_3s : null),
  // 2026-11-03: ThruPlay당 비용(META 동영상 조회 캠페인 과금 단가). ThruPlay 0 = null('—').
  cpv_thruplay: (t) => (t.thruplay > 0 ? t.spend / t.thruplay : null),
  cpv_view: (t) => (t.video_views > 0 ? t.spend / t.video_views : null),
  vtr: (t) =>
    t.video_views > 0 && t.imp > 0 ? (t.video_views / t.imp) * 100 : null,
  atc_rate: (t) =>
    t.click > 0 ? ((t.add_to_cart || 0) / t.click) * 100 : null,
  checkout_rate: (t) =>
    t.add_to_cart > 0
      ? ((t.initiate_checkout || 0) / t.add_to_cart) * 100
      : null,
  purchase_complete_rate: (t) =>
    t.initiate_checkout > 0 ? ((t.cv || 0) / t.initiate_checkout) * 100 : null,
  // tiktok-video-metrics 배치 2 — ad_performance 축(TikTok) 파생 2종.
  completion_rate: (t) =>
    t.video_plays > 0
      ? ((t.video_completions || 0) / t.video_plays) * 100
      : null,
  // avg_play_sec는 합산 불가 — 집계 t의 _video_play_sec_w(행별 avg×plays 누산, B9 sumBase)로
  // plays 가중 평균. 가중치(plays) 0 = null(단순 평균 금지 — spec 배치 2-2).
  video_avg_play_sec: (t) =>
    t.video_plays > 0 ? (t._video_play_sec_w || 0) / t.video_plays : null,
};

// Every ratio requires observed operands; JavaScript null arithmetic is not a measurement.
export const RATIO_INPUTS = {
 ctr:["click","imp"],cvr:["cv","click"],cpc:["spend","click"],cpm:["spend","imp"],
 cpa:["spend","cv"],roas:["rev","spend"],aov:["rev","cv"],
 hook_rate:["video_views_3s","imp"],retention_rate:["thruplay","video_plays"],
 complete_rate:["video_p100","video_plays"],cpv_3s:["spend","video_views_3s"],
 cpv_thruplay:["spend","thruplay"],
 cpv_view:["spend","video_views"],vtr:["video_views","imp"],atc_rate:["add_to_cart","click"],
 checkout_rate:["initiate_checkout","add_to_cart"],purchase_complete_rate:["cv","initiate_checkout"],
 completion_rate:["video_completions","video_plays"],video_avg_play_sec:["_video_play_sec_w","video_plays"],
};
const observed=value => (typeof value==="number"||typeof value==="string") && String(value).trim()!=="" && Number.isFinite(Number(value));
export const RATIO_CALC=Object.fromEntries(Object.entries(RAW_RATIO_CALC).map(([key,calculate])=>[key,totals=>{
 if(!RATIO_INPUTS[key].every(field=>observed(totals[field])))return null;
 const value=calculate(totals);return value!==null&&Number.isFinite(value)?value:null;
}]));

export const ratioOf = (t, id) => (RATIO_CALC[id] ? RATIO_CALC[id](t) : null);

// U-3(X-5) 지표 유형 태그 — additive. RATIO_CALC 산식 무변경.
// volume = 일 합산 가능한 절대량(누적선) / ratio = 기간 집계 파생 비율(일별 바).
// RATIO_CALC 키는 전부 ratio — 키 추가 시 자동 편입(이중 관리 금지).
export const METRIC_KIND = {
  spend: "volume",
  revenue: "volume",
  cv: "volume",
  clicks: "volume",
  impressions: "volume",
  ...Object.fromEntries(Object.keys(RATIO_CALC).map((k) => [k, "ratio"])),
};

export const metricKindOf = (id) => METRIC_KIND[id] || null;

// 목표 달성률 방향 SSOT — cpa/cpc/cpm은 낮을수록 좋음(달성률 = 목표÷실제).
// GoalsPage LOWER_BETTER·exportPPT isLowerBetter와 동일 집합 — 소비처는 이 함수로 수렴.
export const LOWER_BETTER_METRICS = ["cpa", "cpc", "cpm"];
export const goalPct = (metricVal, target, metric) => {
  if (metricVal == null || !(target > 0)) return null;
  if (LOWER_BETTER_METRICS.includes(metric)) {
    // 실적 0(예: 전환 0건 CPA)은 판정 불가 — 0 나눗셈 폭주로 "초과 달성" 오점등 방지
    if (!(metricVal > 0)) return null;
    return Math.round((target / metricVal) * 100);
  }
  return Math.round((metricVal / target) * 100);
};
